/**
 * Server-authoritative playback (pure helpers).
 *
 * Clock: several request/response samples of the server time; the sample with the smallest round trip
 * wins (NTP-style), offset = serverTime − midpoint(send, receive), uncertainty = RTT/2.
 * Position: every client plays the locked round media at (serverNow − startAt); a late joiner starts
 * there, drift is corrected gently (rate) when small and by a single seek when large.
 */
export interface ClockSample {
  sentAt: number;
  receivedAt: number;
  serverTime: number;
}
export interface ClockEstimate {
  offsetMs: number;
  uncertaintyMs: number;
}

export function estimateOffset(samples: ClockSample[]): ClockEstimate | null {
  const ok = samples.filter((s) => Number.isFinite(s.serverTime) && s.receivedAt >= s.sentAt);
  if (ok.length === 0) return null;
  const best = ok.reduce((a, b) => (b.receivedAt - b.sentAt < a.receivedAt - a.sentAt ? b : a));
  const rtt = best.receivedAt - best.sentAt;
  return { offsetMs: Math.round(best.serverTime - (best.sentAt + rtt / 2)), uncertaintyMs: Math.ceil(rtt / 2) };
}

export interface SyncConfig {
  /** Below this |drift| nothing is done (s). */
  deadband: number;
  /** Above this |drift| the player seeks instead of adjusting the rate (s). */
  seekAbove: number;
  /** Max playback-rate change used to catch up or fall back (fraction). */
  maxRateDelta: number;
}
export const DEFAULT_SYNC: SyncConfig = { deadband: 0.15, seekAbove: 1.5, maxRateDelta: 0.05 };

/** Where the video should be now (seconds into the round media), clamped to the media. */
export function expectedPosition(serverNowMs: number, startAtMs: number, durationMs: number): number {
  return Math.max(0, Math.min(durationMs, serverNowMs - startAtMs)) / 1000;
}

export type SyncAction = { kind: 'none' } | { kind: 'rate'; rate: number } | { kind: 'seek'; to: number };

/** drift > 0: the video is behind the server timeline. */
export function syncAction(expected: number, actual: number, cfg: SyncConfig = DEFAULT_SYNC): SyncAction {
  const drift = expected - actual;
  if (Math.abs(drift) >= cfg.seekAbove) return { kind: 'seek', to: expected };
  if (Math.abs(drift) <= cfg.deadband) return { kind: 'rate', rate: 1 };
  // Proportional, bounded: close ~1 s of drift in ≈ 20 s at the max rate delta.
  const rate = 1 + Math.max(-cfg.maxRateDelta, Math.min(cfg.maxRateDelta, drift * 0.05));
  return { kind: 'rate', rate: Math.round(rate * 1000) / 1000 };
}

/** Events revealed so far: those whose media offset the video has passed (server decides `counted`). */
export interface PlaybackEvent {
  offsetMs: number;
  counted: boolean;
}
export function revealed(events: readonly PlaybackEvent[], positionS: number): { shown: number; counted: number } {
  const passed = events.filter((e) => e.offsetMs <= positionS * 1000);
  return { shown: passed.length, counted: passed.filter((e) => e.counted).length };
}
